import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useDarkMode } from './DarkModeContext';
import Footer from './Footer';
import { API_URL } from './config';

const BACKEND_BASE = (API_URL || '').replace(/\/$/, '');

function AccountDeletion() {
    const { darkMode } = useDarkMode();
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState(null); // 'success' | 'error' | null
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setStatus('error');
            return;
        }
        setLoading(true);
        setStatus(null);
        try {
            const res = await fetch(`${BACKEND_BASE}/api/request-account-deletion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: trimmedEmail,
                    name: name.trim() || undefined,
                    message: message.trim() || undefined,
                    source: 'web'
                })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setStatus('success');
                setEmail('');
                setName('');
                setMessage('');
            } else {
                setStatus('error');
            }
        } catch (err) {
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const containerStyle = {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        lineHeight: '1.6',
        color: darkMode ? '#e0e0e0' : '#333',
        backgroundColor: darkMode ? '#1a1a1a' : 'white',
        minHeight: '100vh'
    };
    const linkStyle = { color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' };
    const headingStyle = { color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' };
    const titleUnderlineStyle = { borderBottom: '2px solid #AB5898', paddingBottom: '12px' };
    const inputStyle = {
        width: '100%',
        maxWidth: '400px',
        padding: '12px 14px',
        marginTop: '6px',
        marginBottom: '16px',
        border: `1px solid ${darkMode ? '#444' : '#ccc'}`,
        borderRadius: '10px',
        backgroundColor: darkMode ? '#2a2a2a' : '#fff',
        color: darkMode ? '#e0e0e0' : '#333',
        fontSize: '16px'
    };
    const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: 600 };
    const submitButtonStyle = {
        padding: '14px 28px',
        backgroundColor: '#AB5898',
        color: '#fff',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        marginTop: '8px',
        boxShadow: darkMode ? '0 2px 12px rgba(171, 88, 152, 0.35)' : '0 2px 12px rgba(171, 88, 152, 0.25)',
        transition: 'opacity 0.2s, transform 0.15s'
    };

    return (
        <>
            <Helmet>
                <title>Request account deletion – Merra AI</title>
                <meta name="description" content="Request deletion of your Merra AI account and associated data. We process requests after verification; no automatic deletion." />
                <link rel="canonical" href="https://www.merra.ai/account-deletion" />
                <meta property="og:title" content="Request account deletion – Merra AI" />
                <meta property="og:url" content="https://www.merra.ai/account-deletion" />
            </Helmet>
            <div style={containerStyle}>
                <div style={{ marginBottom: '30px' }}>
                    <Link to="/" style={linkStyle}>← Back to Home</Link>
                </div>

                <h1 style={{ ...headingStyle, ...titleUnderlineStyle, marginBottom: '24px' }}>
                    Request account and data deletion – Merra AI
                </h1>

                <p style={{ color: darkMode ? '#b0b0b0' : '#666', fontStyle: 'italic', marginTop: '8px' }}>
                    Merra AI (“we”, “our”) is the app and developer name shown on the Google Play Store and App Store (Merra AI – AI-powered short-form video creation).
                </p>

                <h2 style={headingStyle}>Procedure to request deletion</h2>
                <p>To request deletion of your account and all associated data, follow these steps:</p>
                <ol style={{ paddingLeft: '24px', marginBottom: '24px' }}>
                    <li>Fill in the form below with the <strong>email address</strong> linked to your Merra AI account.</li>
                    <li>Optionally add your name and a short message (e.g. reason or note).</li>
                    <li>Click <strong>Submit request</strong>.</li>
                    <li>We will receive your request and an administrator will <strong>verify your identity</strong> before performing any deletion. We do <strong>not</strong> delete data automatically.</li>
                    <li>Once verified, we will permanently delete your account and all associated data (see below). We will not retain your data longer than necessary to complete this process.</li>
                </ol>

                <h2 style={headingStyle}>Data we delete</h2>
                <p>After verification, we delete the following in full:</p>
                <ul style={{ paddingLeft: '24px', marginBottom: '24px' }}>
                    <li>Your <strong>account</strong> (profile, email, name, authentication links).</li>
                    <li>All <strong>workspaces</strong> you created or were a member of (where you are the only member).</li>
                    <li>All <strong>content</strong> associated with you: uploaded videos, cuts, generated videos, scripts, voice recordings, and any other files.</li>
                    <li>All <strong>usage and preference data</strong> tied to your account.</li>
                </ul>
                <p><strong>We do not delete data automatically.</strong> Every deletion request is reviewed by an administrator to avoid mistaken or fraudulent requests. Data is only removed after this verification step.</p>

                <h2 style={headingStyle}>Additional retention</h2>
                <p>We may retain minimal, anonymized or aggregated data where required for legal, tax, or security purposes (e.g. transaction records, fraud prevention). Such data will not identify you personally. No additional retention period applies beyond what is necessary for these purposes.</p>

                <h2 style={headingStyle}>Submit your request</h2>
                <form onSubmit={handleSubmit} style={{ marginTop: '16px', maxWidth: '400px' }}>
                    <label style={labelStyle}>Email address (required) *</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        style={inputStyle}
                        disabled={loading}
                    />
                    <label style={labelStyle}>Name (optional)</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        style={inputStyle}
                        disabled={loading}
                    />
                    <label style={labelStyle}>Message (optional)</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Any note or reason for the request"
                        rows={3}
                        style={{ ...inputStyle, minHeight: '80px' }}
                        disabled={loading}
                    />
                    <div style={{ marginTop: '20px' }}>
                        <button
                            type="submit"
                            style={{ ...submitButtonStyle, width: '100%' }}
                            disabled={loading}
                            onMouseOver={(e) => { if (!loading) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                            onMouseOut={(e) => { e.currentTarget.style.opacity = loading ? '0.7' : '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {loading ? 'Sending…' : 'Submit request'}
                        </button>
                    </div>
                </form>

                {status === 'success' && (
                    <p style={{ marginTop: '16px', color: darkMode ? '#7dd87d' : '#2e7d32', fontWeight: 600 }}>
                        Your request has been received. We will process it and delete your account and associated data after verification. You do not need to take any further action.
                    </p>
                )}
                {status === 'error' && (
                    <p style={{ marginTop: '16px', color: '#d32f2f' }}>
                        Something went wrong. Please try again or contact us at <a href="mailto:hello@merra.ai" style={linkStyle}>hello@merra.ai</a>.
                    </p>
                )}

                <h2 style={headingStyle}>Contact</h2>
                <p>For questions about this process or your data: <strong><a href="mailto:hello@merra.ai" style={linkStyle}>hello@merra.ai</a></strong>.</p>

                <Footer />
            </div>
        </>
    );
}

export default AccountDeletion;
